// BusinessFAQSection.jsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Switch,
  Tooltip,
  Skeleton,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer as FAQIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  AutoAwesome as AIIcon
} from '@mui/icons-material';
import { useToggleFaqMutation } from '../../redux/thunks/sp/togglefaq';

// ===== Single FAQ Item =====
const FAQItem = ({ faq, isBusinessOwner, businessId, onToggle }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: faq.isActive ? 'divider' : 'grey.100',
        borderRadius: '8px !important',
        mb: 1,
        opacity: faq.isActive ? 1 : 0.5,
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: '0 0 8px 0' }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ px: 2, py: 0.5, minHeight: 48 }}
      >
        <Box sx={{
          display:    'flex',
          alignItems: 'center',
          gap:        1,
          flex:       1,
          mr:         1,
          minWidth:   0
        }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {faq.question || 'FAQ'}
          </Typography>

          {isBusinessOwner && (
            <Tooltip title={faq.isActive ? 'Hide FAQ' : 'Show FAQ'}>
              <Switch
                size="small"
                checked={faq.isActive}
                onClick={e => { e.stopPropagation(); onToggle(faq._id); }}
                sx={{ ml: 'auto', flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
        <Divider sx={{ mb: 1.5 }} />
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {faq.answer}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5 }}>
          <AIIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            AI-generated · {new Date(faq.publishedAt).toLocaleDateString()}
          </Typography>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// ===== Skeleton =====
export const FAQSkeleton = () => (
  <Box sx={{ mb: 2 }}>
    <Skeleton variant="text" width="30%" height={28} sx={{ mb: 1.5 }} />
    {[1, 2, 3].map(i => (
      <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
    ))}
  </Box>
);

// ===== Main FAQ Section =====
const BusinessFAQSection = ({ profileData, isBusinessOwner }) => {
  const [toggleFaq] = useToggleFaqMutation();

  const allFaqs    = profileData?.faqs || [];
  // Owners see all FAQs (active + inactive)
  // Visitors see only active FAQs
  const visibleFaqs = isBusinessOwner
    ? allFaqs
    : allFaqs.filter(f => f.isActive);

  if (visibleFaqs.length === 0) return null;

  const handleToggle = async (faqId) => {
    try {
      await toggleFaq({
        businessId: profileData._id,
        faqId
      }).unwrap();
    } catch (error) {
      console.error('Failed to toggle FAQ:', error);
    }
  };

  const activeFaqCount = allFaqs.filter(f => f.isActive).length;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, border: 'none' }}>
      {/* Header */}
      <Box sx={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'space-between',
        mb:             2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FAQIcon color="primary" fontSize="small" />
          <Typography variant="h6" component="h2">
            Frequently Asked Questions
          </Typography>
          <Chip
            label={activeFaqCount}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>

        {isBusinessOwner && allFaqs.length > activeFaqCount && (
          <Typography variant="caption" color="text.disabled">
            {allFaqs.length - activeFaqCount} hidden
          </Typography>
        )}
      </Box>

      {/* FAQ Items */}
      {visibleFaqs.map(faq => (
        <FAQItem
          key={faq._id}
          faq={faq}
          isBusinessOwner={isBusinessOwner}
          businessId={profileData._id}
          onToggle={handleToggle}
        />
      ))}

      {/* Owner hint */}
      {isBusinessOwner && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', mt: 1, textAlign: 'center' }}
        >
          FAQs are generated automatically from customer questions and reviews.
          Toggle to show or hide individual FAQs on your public profile.
        </Typography>
      )}
    </Paper>
  );
};

export default BusinessFAQSection;